import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import sk from './sk.json'
import cs from './cs.json'
import en from './en.json'

export const SUPPORTED_LANGUAGES = ['sk', 'cs', 'en'] as const
export type Language = (typeof SUPPORTED_LANGUAGES)[number]

const STORAGE_KEY = 'virtum_lang'

function isSupported(value: string | null | undefined): value is Language {
  return !!value && (SUPPORTED_LANGUAGES as readonly string[]).includes(value)
}

/**
 * Jazyk podľa domény: virtum.cz je česká, virtum.sk slovenská.
 * Vracia null pre domény, ktoré nič nehovoria (localhost, náhľady) - vtedy
 * o jazyku rozhodne prehliadač.
 */
export function languageForHost(hostname: string): Language | null {
  const host = hostname.toLowerCase()
  if (host.endsWith('.cz')) return 'cs'
  if (host.endsWith('.sk')) return 'sk'
  return null
}

/**
 * Poradie je zámerné:
 *   1. vlastná voľba návštevníka (localStorage je per-doména, .sk a .cz sa nemiešajú)
 *   2. doména - na virtum.sk vidí slovenčinu aj návštevník s anglickým prehliadačom,
 *      na virtum.cz češtinu; o jazyku rozhoduje doména, nie nastavenie prehliadača
 *   3. jazyk prehliadača, ak doména nič nehovorí (localhost, náhľady)
 *   4. slovenčina
 */
function resolveInitialLanguage(): Language {
  if (typeof window === 'undefined') return 'sk'

  const saved = localStorage.getItem(STORAGE_KEY)
  if (isSupported(saved)) return saved

  const byHost = languageForHost(window.location.hostname)
  if (byHost) return byHost

  const browser = navigator.language?.split('-')[0]
  return isSupported(browser) ? browser : 'sk'
}

const initial = resolveInitialLanguage()

i18n
  .use(initReactI18next)
  .init({
    resources: {
      sk: { translation: sk },
      cs: { translation: cs },
      en: { translation: en },
    },
    lng: initial,
    fallbackLng: 'sk',
    supportedLngs: [...SUPPORTED_LANGUAGES],
    interpolation: { escapeValue: false },
  })

/** Drží atribút <html lang> v súlade s jazykom - kvôli čítačkám obrazovky aj SEO. */
function syncHtmlLang(lang: Language) {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lang
  }
}

syncHtmlLang(initial)

export function setLanguage(lang: Language) {
  i18n.changeLanguage(lang)
  localStorage.setItem(STORAGE_KEY, lang)
  syncHtmlLang(lang)
}

/** Aktuálny jazyk zredukovaný na podporovanú trojicu. */
export function currentLanguage(): Language {
  const raw = i18n.language?.split('-')[0]
  return isSupported(raw) ? raw : 'sk'
}

export default i18n
